/*
Everything the block builder's three components would otherwise have to decide
for themselves.

ps-admin has no Jest, no Vitest and no RTL, so a component is untestable here by
construction. The rule that follows from that is: components hold JSX and
nothing else, and every decision they would make lives in a plain-node-testable
module. This is that module for BlockPalette / BlockCanvas / BlockInspector.

Three things in particular:

1. `FIELDS` is the inspector's whole content. It is asserted against
   `BLOCK_DEFAULTS` in the tests, key for key, so a form can neither invent a
   prop the serialiser ignores nor quietly drop one the serialiser renders.

2. `previewOf` and `canvasStyle` are the canvas. The canvas IS the email now —
   you drop into it, click a block in it and type into it — so every number it
   draws with comes from `clamp`, `align` and `GUTTER`, shared with the
   serialiser rather than restated. The tests render the same block through
   `previewOf` and through `renderBlocks` and assert the numbers agree, which is
   what stops the editable canvas and the sent email drifting apart.

3. `summaryOf` is the one-line description of a block. It is no longer what the
   canvas draws — the canvas draws the block itself — but the delete prompt
   still has to name what is about to go, and the drag chip still has to say
   what is being dragged.

4. `applyDragEnd` is the drag mechanic. react-beautiful-dnd hands back a result
   object; turning it into the next blocks array is list logic, not view logic.

Nothing here mutates its input: Apollo freezes query results in this admin
(`__DEV__` is undefined in the bundles), so a helper that writes through to a
stored document would throw in production and nowhere else.

CommonJS on purpose: it runs under plain `node src/utils/emailBlockDoc.test.js`.
*/

const {
  BLOCK_TYPES,
  BLOCK_DEFAULTS,
  DEFAULT_SETTINGS,
  GUTTER,
  clamp,
  align,
  socialIcons,
  createBlock,
  insertBlock,
  moveBlock,
} = require('./emailBlocks');
const { stripOverriddenDeclarations, overrideClassName } = require('./emailTypographyOverride');

// --- the palette ----------------------------------------------------------

// Grouped the way the CMS page builder groups its modules (Anchors / Text /
// Media / Extra in Views/DragAndDrop/ItemsModules/items.js) rather than by
// inventing a taxonomy: what the block does to the page, not what it is made
// of. Every type in BLOCK_TYPES appears in exactly one group — the tests
// enforce it, because a type that falls out of the palette is a type nobody can
// add.
const PALETTE_GROUPS = [
  { key: 'text', label: 'Text', types: ['heading', 'text', 'button'] },
  { key: 'media', label: 'Media', types: ['image', 'social'] },
  { key: 'layout', label: 'Layout', types: ['divider', 'spacer'] },
];

// One-line descriptions for the palette tiles. Purely editorial.
const BLOCK_BLURBS = {
  heading: 'A line of large bold type',
  text: 'A paragraph of rich text',
  button: 'A linked call to action',
  image: 'A picture, optionally linked',
  social: 'A row of linked social icons',
  divider: 'A horizontal rule',
  spacer: 'Vertical breathing room',
};

const labelFor = (type) => {
  const found = BLOCK_TYPES.find(entry => entry.type === type);
  return found ? found.label : type;
};

// A drag out of the palette carries a draggableId; a drag inside the canvas
// carries a block id. Both live in one DragDropContext, so they need namespaces
// that cannot collide. Block ids are `b<random>-<n>` (see emailBlocks.js), so
// the `palette:` prefix is enough.
const PALETTE_PREFIX = 'palette:';
const CANVAS_DROPPABLE = 'canvas';

const paletteDraggableId = type => `${PALETTE_PREFIX}${type}`;
const paletteDroppableId = groupKey => `${PALETTE_PREFIX}${groupKey}`;

const typeFromDraggableId = (draggableId) => {
  if (typeof draggableId !== 'string' || draggableId.indexOf(PALETTE_PREFIX) !== 0) return null;
  const type = draggableId.slice(PALETTE_PREFIX.length);
  return BLOCK_DEFAULTS[type] ? type : null;
};

// --- the document ---------------------------------------------------------

const isPlainObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

// A brand-new, mutable empty document. EMPTY_DOC itself is frozen — handing it
// out would make the first edit throw.
const newDoc = () => ({
  version: 1,
  settings: { ...DEFAULT_SETTINGS },
  blocks: [],
});

/*
Turns whatever the server sent into a document the editor can hold, or null.

null is the load-bearing answer: it means "this template is not block-authored",
which is true of all 78 live templates and is what keeps them on the cell and
HTML editors. Anything that is not a usable document — absent, a string that
does not parse, an object with no blocks array — reads as not-block-authored
rather than as an error, because opening a legacy template must never show a
broken builder.

`blocks` arrives as a JSON scalar, but a String column is a live possibility on
this schema, so a JSON string is parsed rather than refused.
*/
const docFromTemplate = (template) => {
  const raw = template && template.blocks;
  if (raw === null || raw === undefined) return null;

  let parsed = raw;
  if (typeof raw === 'string') {
    if (!raw.trim()) return null;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  if (!isPlainObject(parsed) || !Array.isArray(parsed.blocks)) return null;

  const settings = isPlainObject(parsed.settings) ? parsed.settings : {};
  return {
    version: 1,
    // Key by key off the defaults, so a document saved before a setting existed
    // opens with that setting rather than with `undefined` in an input.
    settings: {
      width: settings.width === undefined ? DEFAULT_SETTINGS.width : settings.width,
      background: settings.background || DEFAULT_SETTINGS.background,
      cardBackground: settings.cardBackground || DEFAULT_SETTINGS.cardBackground,
      fontFamily: settings.fontFamily || DEFAULT_SETTINGS.fontFamily,
      fontSize: settings.fontSize === undefined ? DEFAULT_SETTINGS.fontSize : settings.fontSize,
      lineHeight: settings.lineHeight === undefined ? DEFAULT_SETTINGS.lineHeight : settings.lineHeight,
      textColor: settings.textColor || DEFAULT_SETTINGS.textColor,
      linkColor: settings.linkColor || DEFAULT_SETTINGS.linkColor,
    },
    blocks: parsed.blocks.filter(isPlainObject).map(block => ({ ...block })),
  };
};

// Stable JSON: keys in a fixed order, so the dirty check compares documents and
// not the order Apollo happened to hand the keys back in. The same string is
// what gets sent as `blocksJson`.
const stableStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (isPlainObject(value)) {
    const keys = Object.keys(value).sort();
    return `{${keys.map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value === undefined ? null : value);
};

const docToJson = doc => stableStringify(doc);

const docIsDirty = (doc, original) => {
  if (!doc && !original) return false;
  if (!doc || !original) return true;
  return docToJson(doc) !== docToJson(original);
};

const withBlocks = (doc, blocks) => ({ ...doc, blocks });

const withSettings = (doc, patch) => ({ ...doc, settings: { ...doc.settings, ...patch } });

const withBlockProps = (doc, id, patch) => withBlocks(
  doc,
  doc.blocks.map(block => (block.id === id ? { ...block, ...patch } : block)),
);

const findBlock = (doc, id) => (doc && id
  ? doc.blocks.find(block => block.id === id) || null
  : null);

// --- drag ------------------------------------------------------------------

/*
The whole drag mechanic, as a function of the blocks array and what
react-beautiful-dnd reported.

Three outcomes and no others: a drop outside any list changes nothing, a drop
from a palette group inserts a fresh block at that index, and a drop inside the
canvas reorders. `moveBlock` is a no-op for an out-of-range index, so a drag
that rbd reports oddly cannot corrupt the list.
*/
const applyDragEnd = (blocks, result) => {
  const source = result && result.source;
  const destination = result && result.destination;
  if (!source || !destination) return Array.isArray(blocks) ? blocks.slice() : [];

  if (destination.droppableId !== CANVAS_DROPPABLE) return blocks.slice();

  if (source.droppableId === CANVAS_DROPPABLE) {
    return moveBlock(blocks, source.index, destination.index);
  }

  const type = typeFromDraggableId(result.draggableId);
  if (!type) return blocks.slice();
  return insertBlock(blocks, createBlock(type), destination.index);
};

// --- the inspector's forms -------------------------------------------------

const ALIGN_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Centre' },
  { value: 'right', label: 'Right' },
];

const LEVEL_OPTIONS = [
  { value: 1, label: 'H1 — biggest' },
  { value: 2, label: 'H2' },
  { value: 3, label: 'H3 — smallest' },
];

// The stacks that survive every mail client without a webfont request. Anything
// stored that is not on this list is offered back as its own option by
// `optionsFor`, so opening a document never silently rewrites its font.
const FONT_OPTIONS = [
  { value: "Arial, 'Helvetica Neue', Helvetica, sans-serif", label: 'Arial' },
  { value: "'Helvetica Neue', Helvetica, Arial, sans-serif", label: 'Helvetica' },
  { value: "Georgia, 'Times New Roman', Times, serif", label: 'Georgia' },
  { value: "'Trebuchet MS', 'Lucida Grande', Tahoma, sans-serif", label: 'Trebuchet' },
  { value: 'Verdana, Geneva, Tahoma, sans-serif', label: 'Verdana' },
  { value: "'Courier New', Courier, monospace", label: 'Courier' },
];

// The same stacks, plus the empty choice a BLOCK's font-family has and the
// document's does not: a block that says nothing about its font uses the
// document's, which is what every heading and paragraph saved before these
// props existed does.
const INHERIT_FONT_OPTIONS = [{ value: '', label: 'Same as the email' }].concat(FONT_OPTIONS);

const INHERIT_HINT = 'Leave empty to use the email’s own setting.';

/*
One form per block type, listing exactly the props the serialiser reads for that
type — the tests assert `FIELDS[type]` keys against `BLOCK_DEFAULTS[type]` keys
in both directions, so an invented field or a forgotten one fails the build.

`min`/`max` are the serialiser's own clamps, restated here as input attributes
so the number spinners stop where the render stops. They are NOT enforced on
typed input: clamping mid-keystroke makes a field impossible to clear and retype.
The serialiser clamps on render regardless, which is where it has to happen.
*/
const FIELDS = {
  heading: [
    { key: 'text', label: 'Text', kind: 'text' },
    { key: 'level', label: 'Level', kind: 'select', options: LEVEL_OPTIONS, numeric: true },
    { key: 'align', label: 'Align', kind: 'select', options: ALIGN_OPTIONS },
    { key: 'color', label: 'Colour', kind: 'color' },
    { key: 'fontSize', label: 'Size', kind: 'number', min: 8, max: 96, unit: 'px' },
    { key: 'lineHeight', label: 'Line height', kind: 'number', min: 8, max: 200, unit: 'px', hint: 'Leave empty for 1.25× the size, which is what a heading has always used.' },
    { key: 'fontFamily', label: 'Font', kind: 'select', options: INHERIT_FONT_OPTIONS },
  ],
  text: [
    { key: 'html', label: 'Rich text', kind: 'richtext' },
    { key: 'align', label: 'Align', kind: 'select', options: ALIGN_OPTIONS },
    { key: 'fontSize', label: 'Size', kind: 'number', min: 8, max: 96, unit: 'px', hint: INHERIT_HINT },
    { key: 'lineHeight', label: 'Line height', kind: 'number', min: 8, max: 200, unit: 'px', hint: INHERIT_HINT },
    { key: 'color', label: 'Colour', kind: 'color', hint: INHERIT_HINT },
    { key: 'fontFamily', label: 'Font', kind: 'select', options: INHERIT_FONT_OPTIONS },
  ],
  button: [
    { key: 'label', label: 'Label', kind: 'text' },
    { key: 'href', label: 'Links to', kind: 'text', hint: 'Handlebars such as {{{ctaLink}}} pass through untouched.' },
    { key: 'background', label: 'Background', kind: 'color' },
    { key: 'color', label: 'Text colour', kind: 'color' },
    { key: 'radius', label: 'Corner radius', kind: 'number', min: 0, max: 60, unit: 'px' },
    { key: 'align', label: 'Align', kind: 'select', options: ALIGN_OPTIONS },
    { key: 'paddingX', label: 'Padding — sides', kind: 'number', min: 0, max: 120, unit: 'px' },
    { key: 'paddingY', label: 'Padding — top/bottom', kind: 'number', min: 0, max: 60, unit: 'px' },
  ],
  image: [
    // `image` rather than `text`: the inspector draws a thumbnail and a media
    // library button above the URL. The URL itself stays an editable text field
    // inside that control, because a converted PRO hero's src is `{{{imageUrl}}}`
    // and a picker alone would make it unreadable and unsettable.
    { key: 'src', label: 'Image URL', kind: 'image', hint: 'Handlebars such as {{{imageUrl}}} pass through untouched — the campaign chooses the picture at send time.' },
    { key: 'alt', label: 'Alt text', kind: 'text', hint: 'Read out when images are blocked, which is most of the time in Outlook. Filled in from the asset description when you pick one and have not written your own.' },
    { key: 'href', label: 'Links to', kind: 'text', hint: 'Leave empty for an image that is not a link.' },
    { key: 'width', label: 'Width', kind: 'number', min: 1, max: 900, unit: 'px' },
    { key: 'align', label: 'Align', kind: 'select', options: ALIGN_OPTIONS },
  ],
  social: [
    // `icons` is a LIST, not a scalar, so it gets a control kind of its own —
    // the inspector draws one row of inputs per icon with add / remove / reorder
    // beside them. Every other kind here edits one value.
    {
      key: 'icons',
      label: 'Icons',
      kind: 'icons',
      hint: 'One row per icon, left to right. An icon with no image URL is not sent.',
      // The per-icon controls, in the order the row draws them. Same descriptor
      // shape as a block field, so the inspector reuses the same <Field>.
      fields: [
        { key: 'src', label: 'Image URL', kind: 'text' },
        { key: 'href', label: 'Links to', kind: 'text' },
        { key: 'alt', label: 'Alt text', kind: 'text' },
        { key: 'width', label: 'Size', kind: 'number', min: 1, max: 200, unit: 'px' },
        { key: 'background', label: 'Chip colour', kind: 'color', hint: 'The rounded square behind the icon. Empty for none.' },
        { key: 'radius', label: 'Chip radius', kind: 'number', min: 0, max: 60, unit: 'px' },
      ],
    },
    { key: 'align', label: 'Align', kind: 'select', options: ALIGN_OPTIONS },
    { key: 'gap', label: 'Space between', kind: 'number', min: 0, max: 40, unit: 'px' },
  ],
  divider: [
    { key: 'color', label: 'Colour', kind: 'color' },
    { key: 'thickness', label: 'Thickness', kind: 'number', min: 1, max: 20, unit: 'px' },
    { key: 'spacing', label: 'Space around', kind: 'number', min: 0, max: 80, unit: 'px' },
  ],
  spacer: [
    { key: 'height', label: 'Height', kind: 'number', min: 1, max: 200, unit: 'px' },
  ],
};

// The panel shown when nothing is selected. Same shape, over `doc.settings`.
const SETTINGS_FIELDS = [
  { key: 'width', label: 'Email width', kind: 'number', min: 320, max: 900, unit: 'px', hint: '600px is what almost every template in this account uses.' },
  { key: 'background', label: 'Page background', kind: 'color', hint: 'Behind the card, where the client shows it.' },
  { key: 'cardBackground', label: 'Card background', kind: 'color' },
  { key: 'fontFamily', label: 'Font', kind: 'select', options: FONT_OPTIONS },
  { key: 'fontSize', label: 'Body size', kind: 'number', min: 8, max: 96, unit: 'px', hint: 'Every text block that does not set its own.' },
  { key: 'lineHeight', label: 'Body line height', kind: 'number', min: 8, max: 200, unit: 'px' },
  { key: 'textColor', label: 'Body text', kind: 'color' },
  { key: 'linkColor', label: 'Links', kind: 'color' },
];

// A select whose stored value is not one of its options would render blank and
// then overwrite that value on the next change. Offer the stored value back
// instead.
const optionsFor = (field, value) => {
  const options = field.options || [];
  if (options.some(option => option.value === value)) return options;
  if (value === undefined || value === null || value === '') return options;
  return options.concat([{ value, label: `${value} (as stored)` }]);
};

/*
What a form control's value becomes on the block.

Numbers stay numbers, so the document round-trips through JSON as the serialiser
expects. An empty number field stays the empty string rather than becoming 0 or
NaN: it is a field mid-edit, and the serialiser already falls back to the type's
default for anything non-finite, so the render stays sane while it is empty.
*/
const coerceFieldValue = (field, raw) => {
  const isNumeric = field.kind === 'number' || field.numeric === true;
  if (!isNumeric) return raw;
  if (raw === '' || raw === null || raw === undefined) return '';
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : raw;
};

// --- the canvas ------------------------------------------------------------

// Auto margins, because neither `text-align` on the cell nor the td's `align`
// attribute reliably centres a block-level box with an explicit width.
//
// That was not a theory: a 390px logo drew centred here and arrived flush left in
// a real inbox. The serialiser was relying on `align` and this preview was relying
// on the margins, so the two disagreed and only the email was wrong. The serialiser
// now emits the same margins — see imageMargin() in emailBlocks.js — and the
// agreement test holds them together.
const blockMargin = (alignment) => {
  if (alignment === 'center') return { marginLeft: 'auto', marginRight: 'auto' };
  if (alignment === 'right') return { marginLeft: 'auto', marginRight: 0 };
  return { marginLeft: 0, marginRight: 'auto' };
};

/*
The block as it will look, not a card with the word "button" on it.

Each branch mirrors one renderer in emailBlocks.js — the same clamps, the same
cell padding, the same inline style values — because the canvas is not a preview
of the email any more, it IS the editing surface: blocks are dropped into it,
selected in it and typed into it. If it drifts from the serialiser the user is
editing one email and sending another.

Returns `{ kind, cellStyle, ... }`. The caller switches on `kind` and does no
arithmetic of its own.
*/
const previewOf = (block, rawSettings) => {
  const settings = { ...DEFAULT_SETTINGS, ...(rawSettings || {}) };
  const type = block && block.type;

  // Empty means "the document's" on both sides — mirrors `typeFace` in the
  // serialiser rather than restating the rule.
  const face = (typeof block.fontFamily === 'string' && block.fontFamily.trim())
    ? block.fontFamily.trim()
    : settings.fontFamily;

  if (type === 'heading') {
    const size = clamp(block.fontSize, 8, 96, BLOCK_DEFAULTS.heading.fontSize);
    return {
      kind: 'heading',
      level: clamp(block.level, 1, 3, 2),
      text: block.text === undefined || block.text === null ? '' : String(block.text),
      cellStyle: { padding: `16px ${GUTTER}px 8px ${GUTTER}px` },
      style: {
        margin: 0,
        fontFamily: face,
        fontSize: `${size}px`,
        lineHeight: `${clamp(block.lineHeight, 8, 200, Math.round(size * 1.25))}px`,
        fontWeight: 'bold',
        color: block.color || settings.textColor,
        textAlign: align(block.align, 'left'),
      },
    };
  }

  if (type === 'text') {
    const stored = block.html === undefined || block.html === null ? '' : String(block.html);
    return {
      kind: 'text',
      // What the canvas DRAWS: the same string `renderText` emits, with the
      // declarations the container duplicates taken out of it — shared helper,
      // so the editing surface and the inbox cannot disagree.
      html: stripOverriddenDeclarations(stored, block),
      // What the EDITOR edits: the stored string, untouched. Tiptap writes back
      // what it was handed on the next keystroke, and handing it the stripped
      // version would rewrite the document and make clearing a property unable
      // to restore what the paragraph had.
      editHtml: stored,
      // One class per override, for the rules in emails.css that neutralise the
      // declarations still sitting on the DOM while the caret is in the block.
      overrideClass: overrideClassName(block),
      cellStyle: { padding: `8px ${GUTTER}px` },
      style: {
        fontFamily: face,
        fontSize: `${clamp(block.fontSize, 8, 96, clamp(settings.fontSize, 8, 96, DEFAULT_SETTINGS.fontSize))}px`,
        lineHeight: `${clamp(block.lineHeight, 8, 200, clamp(settings.lineHeight, 8, 200, DEFAULT_SETTINGS.lineHeight))}px`,
        color: block.color || settings.textColor,
        textAlign: align(block.align, 'left'),
      },
    };
  }

  if (type === 'button') {
    const alignment = align(block.align, 'center');
    return {
      kind: 'button',
      label: block.label === undefined || block.label === null ? '' : String(block.label),
      href: block.href || '',
      cellStyle: { padding: `16px ${GUTTER}px`, textAlign: alignment },
      style: {
        display: 'inline-block',
        backgroundColor: block.background || BLOCK_DEFAULTS.button.background,
        color: block.color || BLOCK_DEFAULTS.button.color,
        borderRadius: `${clamp(block.radius, 0, 60, BLOCK_DEFAULTS.button.radius)}px`,
        padding: `${clamp(block.paddingY, 0, 60, BLOCK_DEFAULTS.button.paddingY)}px ${clamp(block.paddingX, 0, 120, BLOCK_DEFAULTS.button.paddingX)}px`,
        fontFamily: settings.fontFamily,
        fontSize: '16px',
        lineHeight: '120%',
        fontWeight: 'bold',
        textDecoration: 'none',
        textAlign: 'center',
      },
    };
  }

  if (type === 'image') {
    const alignment = align(block.align, 'center');
    const width = clamp(block.width, 1, clamp(settings.width, 320, 900, DEFAULT_SETTINGS.width), clamp(settings.width, 320, 900, DEFAULT_SETTINGS.width));
    return {
      kind: 'image',
      src: block.src || '',
      alt: block.alt || '',
      href: typeof block.href === 'string' ? block.href.trim() : '',
      cellStyle: { padding: 0, textAlign: alignment },
      style: {
        border: 0,
        display: 'block',
        width: `${width}px`,
        maxWidth: '100%',
        height: 'auto',
        ...blockMargin(alignment),
      },
    };
  }

  if (type === 'social') {
    const alignment = align(block.align, 'center');
    const gap = clamp(block.gap, 0, 40, BLOCK_DEFAULTS.social.gap);
    const side = Math.round(gap / 2);
    return {
      kind: 'social',
      align: alignment,
      // Only the icons the serialiser will actually emit — an icon with no src
      // is dropped there, and drawing it here would show the user a row the
      // email does not have.
      icons: socialIcons(block).map((icon) => {
        const width = clamp(icon.width, 1, 200, 20);
        const radius = clamp(icon.radius, 0, 60, 0);
        const background = typeof icon.background === 'string' ? icon.background.trim() : '';
        return {
          src: icon.src,
          alt: icon.alt || '',
          href: typeof icon.href === 'string' ? icon.href.trim() : '',
          width,
          cellStyle: { padding: `0 ${side}px`, verticalAlign: 'middle' },
          chipStyle: {
            borderCollapse: 'collapse',
            width: `${width}px`,
            ...(background ? { backgroundColor: background } : {}),
            ...(radius ? { borderRadius: `${radius}px` } : {}),
          },
          // height:auto, matching renderSocial. This said `${width}px` and was the
          // SECOND copy of the same wrong assumption — that a social icon is
          // square. It is not: PS's footer ends with a wide RADIO PRIMAVERA SOUND
          // wordmark, which a square box stretches into a smear. Fixing only the
          // serialiser left the canvas still drawing it wrong, so the editor and
          // the sent email disagreed about the same block.
          style: { border: 0, display: 'block', width: `${width}px`, height: 'auto' },
        };
      }),
      cellStyle: { padding: `8px ${GUTTER}px`, textAlign: alignment },
      // The row's own table. The per-icon numbers live on `icons` above rather
      // than here, because there is one set of them per icon and `style` is the
      // one thing the declaration sweep in the tests compares against the
      // serialiser's row.
      style: { borderCollapse: 'collapse' },
    };
  }

  if (type === 'divider') {
    const spacing = clamp(block.spacing, 0, 80, BLOCK_DEFAULTS.divider.spacing);
    return {
      kind: 'divider',
      cellStyle: { padding: `${spacing}px ${GUTTER}px` },
      style: {
        borderTop: `${clamp(block.thickness, 1, 20, BLOCK_DEFAULTS.divider.thickness)}px solid ${block.color || BLOCK_DEFAULTS.divider.color}`,
        fontSize: 0,
        lineHeight: 0,
        height: 0,
      },
    };
  }

  if (type === 'spacer') {
    const height = clamp(block.height, 1, 200, BLOCK_DEFAULTS.spacer.height);
    return {
      kind: 'spacer',
      height,
      cellStyle: { padding: 0 },
      style: { height: `${height}px`, fontSize: 0, lineHeight: 0 },
    };
  }

  // An unknown type is a document from a newer build of the admin, or a hand
  // edit. Say so in the canvas rather than rendering nothing and looking broken.
  return { kind: 'unknown', type: String(type), cellStyle: { padding: `8px ${GUTTER}px` }, style: {} };
};

// The card the blocks sit on, so the canvas shows the document's own background
// and width rather than the CMS's paper.
const canvasStyle = (rawSettings) => {
  const settings = { ...DEFAULT_SETTINGS, ...(rawSettings || {}) };
  return {
    page: { backgroundColor: settings.background },
    card: {
      backgroundColor: settings.cardBackground,
      maxWidth: `${clamp(settings.width, 320, 900, DEFAULT_SETTINGS.width)}px`,
    },
  };
};

// Which block types can be authored with the caret inside the canvas, and which
// only have properties. The canvas asks this rather than restating the switch:
// a type that grows an in-place editor must not need the canvas edited too.
//
// `text` is Tiptap in the block itself, `heading` and `button` are one line of
// plain text each. Everything else — an image URL, a rule's colour, a spacer's
// height — is a property, not content you can put a caret in.
const INLINE_EDITABLE = {
  heading: 'text',
  text: 'html',
  button: 'label',
};

const inlineFieldFor = (block) => {
  const type = block && block.type;
  return Object.prototype.hasOwnProperty.call(INLINE_EDITABLE, type) ? INLINE_EDITABLE[type] : null;
};

// --- the row summary -------------------------------------------------------

/*
A block named in one line, in words.

This is NOT what the canvas draws — `previewOf` is, and the canvas draws the
block itself, at its own size, in its own colours. This is for the places that
need to *name* a block rather than show it: the delete prompt ("Delete the
button “Buy tickets”?"), and the label on a block's own toolbar, where the type
alone is not enough to tell two headings apart. Nothing here returns a colour,
a font or an image.

Returns `{ text, meta, isEmpty }`:

  text     the identifying line — the heading's words, the paragraph's first
           line, the button's label, the image's alt or filename, "1px rule",
           "24px space". When there is nothing authored yet this is a
           placeholder ("Empty heading") and `isEmpty` is true, so the row can
           grey it out rather than showing a blank.
  meta     the secondary fact, when there is one: where a button links, the
           filename behind an alt text, a heading's level. '' otherwise.
  isEmpty  true when the block holds no authored content.

Both strings are flattened (tags stripped, whitespace collapsed) and clipped, so
a row stays one line whatever someone pasted into it.
*/

const SUMMARY_MAX = 48;
const META_MAX = 44;

// Tags stripped rather than parsed: this is a label, not a render, and the one
// caller shows it as plain text.
const flatten = value => String(value === undefined || value === null ? '' : value)
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const clip = (value, max) => (value.length > max ? `${value.slice(0, max - 1)}…` : value);

// The FIRST LINE of a paragraph, not all of it. Block closes and <br> become
// line breaks first, so a text block of six paragraphs is summarised by its
// opening sentence instead of by all six run together.
//
// The break marker is a NUL rather than a newline because the stored markup is
// full of literal newlines that are only whitespace to a mail client — these
// cells arrive with them inside opening tags — and splitting on those would end
// the summary at whatever byte the exporter happened to wrap on.
const LINE_BREAK = '\u0000';

const firstLine = (html) => {
  const broken = String(html === undefined || html === null ? '' : html)
    .replace(/<br\s*\/?>/gi, LINE_BREAK)
    .replace(/<\/(p|div|h[1-6]|li|ul|ol|blockquote)\s*>/gi, LINE_BREAK);
  const lines = broken.split(LINE_BREAK).map(flatten).filter(Boolean);
  return lines.length ? lines[0] : '';
};

// The last path segment of a URL, without its query string. `{{{heroImage}}}`
// has no segments and comes back whole, which is the useful answer.
const fileName = (src) => {
  const clean = String(src || '').trim().split(/[?#]/)[0];
  const parts = clean.split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : clean;
};

const summary = (text, meta, isEmpty) => ({
  text: clip(text, SUMMARY_MAX),
  meta: clip(meta || '', META_MAX),
  isEmpty: Boolean(isEmpty),
});

const summaryOf = (block) => {
  if (!block) return summary('', '', true);

  switch (block.type) {
    case 'heading': {
      const text = flatten(block.text);
      const level = clamp(block.level, 1, 3, 2);
      return summary(text || 'Empty heading', `H${level}`, !text);
    }
    case 'text': {
      const text = firstLine(block.html);
      return summary(text || 'Empty text block', '', !text);
    }
    case 'button': {
      const label = flatten(block.label);
      const href = flatten(block.href);
      return summary(label || 'Unlabelled button', href, !label);
    }
    case 'image': {
      const alt = flatten(block.alt);
      const name = flatten(fileName(block.src));
      if (!name && !alt) return summary('No image yet', '', true);
      // Alt text is the human answer where there is one, and the filename is
      // then the fact behind it. With no alt the filename IS the answer.
      return summary(alt || name, alt ? name : '', false);
    }
    case 'social': {
      // Named by the icons that will actually be sent, not by the list length:
      // a row of four where one has no URL is a row of three in the inbox.
      const icons = socialIcons(block);
      if (icons.length === 0) return summary('Empty social row', 'sent as nothing', true);
      const names = icons
        .map(icon => flatten(icon.alt) || flatten(fileName(icon.src)))
        .filter(Boolean)
        .join(', ');
      return summary(`${icons.length} social icon${icons.length === 1 ? '' : 's'}`, names, false);
    }
    case 'divider':
      return summary(`${clamp(block.thickness, 1, 20, BLOCK_DEFAULTS.divider.thickness)}px rule`, '', false);
    case 'spacer':
      return summary(`${clamp(block.height, 1, 200, BLOCK_DEFAULTS.spacer.height)}px space`, '', false);
    default:
      // A document from a newer build of the admin, or a hand edit. Say so
      // rather than rendering an empty row that looks broken.
      return summary(`Unknown block type “${flatten(block.type)}”`, '', false);
  }
};

// What the delete prompt quotes back. A block with nothing in it has no name to
// quote, so it gets the empty string and the prompt falls back to "this image".
const blockSummary = (block) => {
  const { text, isEmpty } = summaryOf(block);
  return isEmpty ? '' : text;
};

module.exports = {
  PALETTE_GROUPS,
  BLOCK_BLURBS,
  CANVAS_DROPPABLE,
  FIELDS,
  SETTINGS_FIELDS,
  FONT_OPTIONS,
  labelFor,
  paletteDraggableId,
  paletteDroppableId,
  typeFromDraggableId,
  newDoc,
  docFromTemplate,
  docToJson,
  docIsDirty,
  withBlocks,
  withSettings,
  withBlockProps,
  findBlock,
  applyDragEnd,
  optionsFor,
  coerceFieldValue,
  previewOf,
  canvasStyle,
  INLINE_EDITABLE,
  inlineFieldFor,
  summaryOf,
  blockSummary,
};
